import React from 'react';
import { motion } from 'framer-motion';

const sectionTitleClass = 'text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white';
const paragraphClass = 'text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed';

const policySections = [
  {
    title: 'Information We Collect',
    description:
      'We collect information that helps us deliver secure, high-quality engineering services. This includes contact details, company information, project context, usage analytics, and any content you choose to share with us.',
    bullets: [
      'Business contact information submitted through forms or meetings',
      'Engagement records for onboarding, delivery, and support',
      'Telemetry from our websites, portals, and support tools',
      'Security and compliance artifacts required to fulfill contracts'
    ]
  },
  {
    title: 'How We Use Your Information',
    description:
      'We only use collected data to provide services, improve our platforms, and uphold safety & compliance commitments. We never sell your data to third parties.',
    bullets: [
      'Deliver proposals, statements of work, and project updates',
      'Enable secure collaboration and platform access',
      'Improve our digital experiences through aggregated analytics',
      'Meet legal, regulatory, and audit requirements'
    ]
  },
  {
    title: 'Data Protection & Retention',
    description:
      'Our security program combines enterprise-grade infrastructure, encryption, and governance to protect client data across regions.',
    bullets: [
      'Role-based access controls, MFA, and continuous monitoring',
      'Encryption in transit and at rest for customer data',
      'Retention aligned with contractual obligations and regulations',
      'Third-party vendor due diligence and annual reviews'
    ]
  }
];

const rights = [
  'Request a copy of the information we maintain about you',
  'Ask us to update, correct, or delete personal data',
  'Limit processing for specific use cases when possible',
  'File a complaint with your local supervisory authority'
];

const heroImage = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=60';

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Hero Banner Section */}
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Privacy Policy"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/40 dark:border-slate-700 text-sm font-semibold mb-4"
          >
            Privacy Policy
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white"
          >
            Transparent data practices for trusted engineering partnerships
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl text-white/85 leading-relaxed"
          >
            This policy explains the information we collect, how we use it, and the controls
            available to you when working with FirstLogic Technologies.
          </motion.p>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10">
          {policySections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-8 shadow-sm"
            >
              <h2 className={sectionTitleClass}>{section.title}</h2>
              <p className={`mt-4 ${paragraphClass}`}>{section.description}</p>
              <ul className="mt-6 space-y-3">
                {section.bullets.map((bullet) => (
                  <li key={bullet} className="flex gap-3 text-slate-600 dark:text-slate-300">
                    <span className="mt-2 h-2 w-2 rounded-full bg-blue-500 inline-block" />
                    <span className="flex-1">{bullet}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-8 shadow-sm"
          >
            <h2 className={sectionTitleClass}>Your Data Rights</h2>
            <p className={`mt-3 ${paragraphClass}`}>
              Depending on your region, you may exercise the following rights. We respond to all
              verified requests within the timelines defined by applicable regulation.
            </p>
            <ul className="mt-6 space-y-3">
              {rights.map((right) => (
                <li key={right} className="flex gap-3 text-slate-600 dark:text-slate-300">
                  <span className="mt-2 h-2 w-2 rounded-full bg-blue-500 inline-block" />
                  <span className="flex-1">{right}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-blue-50 dark:bg-slate-900/80 p-8 shadow-sm"
          >
            <h2 className={sectionTitleClass}>Contact & Updates</h2>
            <p className={`mt-3 ${paragraphClass}`}>
              Email privacy@firstlogic.tech for any privacy questions. We will update this policy as
              our services evolve and will post the latest version here with an updated effective
              date.
            </p>
            <div className="mt-6 space-y-2 text-slate-700 dark:text-slate-200">
              <p><span className="font-semibold text-slate-900 dark:text-white">Effective date:</span> November 20, 2025</p>
              <p><span className="font-semibold text-slate-900 dark:text-white">Address:</span> 123 Innovation Way, Austin, TX</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;

